import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import JobCard from "@/components/JobCard";
import AdSlot from "@/components/AdSlot";
import { useI18n } from "@/contexts/I18nContext";
import { Search, MapPin, BadgeCheck } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function Jobs() {
  const { t, lang } = useI18n();
  const [sp, setSp] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [cats, setCats] = useState([]);
  const [loc, setLoc] = useState(sp.get("location") || "");
  const [q, setQ] = useState(sp.get("q") || "");
  const [verifiedOnly, setVerifiedOnly] = useState(sp.get("verified") === "1");
  const active = sp.get("category") || "";
  const queryParam = sp.get("q") || "";

  useSEO({
    title: active
      ? `${active.charAt(0).toUpperCase() + active.slice(1)} Jobs${loc ? ` in ${loc}` : ""} | Sarthi Associates`
      : "Browse Frontline Jobs — Welder, Fitter, Electrician, Plumber, Rigger | Sarthi Associates",
    description: `Browse verified ${active || "frontline"} jobs${loc ? ` in ${loc}` : ""} — apply in 30 seconds. Sarthi Associates connects industrial workers with EPC contractors across India.`,
    path: `/jobs${window.location.search}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://sarthiassociates.com/" },
        { "@type": "ListItem", "position": 2, "name": "Jobs", "item": "https://sarthiassociates.com/jobs" },
        ...(active
          ? [{ "@type": "ListItem", "position": 3, "name": `${active.charAt(0).toUpperCase() + active.slice(1)} jobs`, "item": `https://sarthiassociates.com/jobs?category=${active}` }]
          : []),
      ],
    },
  });

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const params = {};
    if (active) params.category = active;
    if (loc) params.location = loc;
    api.get("/jobs", { params }).then((r) => setJobs(r.data));
  }, [active, loc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep local search input synced when q param changes externally (e.g. from landing search)
  useEffect(() => { setQ(queryParam); }, [queryParam]);

  const applySearch = (e) => {
    e?.preventDefault?.();
    const n = new URLSearchParams(sp);
    if (q.trim()) n.set("q", q.trim()); else n.delete("q");
    setSp(n);
  };

  const nameFor = (c) => c[`name_${lang}`] || c.name_en;

  const toggleVerifiedOnly = () => {
    const next = !verifiedOnly;
    setVerifiedOnly(next);
    const n = new URLSearchParams(sp);
    if (next) n.set("verified", "1"); else n.delete("verified");
    setSp(n);
  };

  // Client-side filter for free-text query across title/description/location/category
  const visibleJobs = (queryParam
    ? jobs.filter((j) => {
        const hay = `${j.title || ""} ${j.description || ""} ${j.location || ""} ${j.category || ""}`.toLowerCase();
        return hay.includes(queryParam.toLowerCase());
      })
    : jobs
  ).filter((j) => (verifiedOnly ? j.contractor_verified : true));

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <h1 className="font-display text-3xl font-extrabold sm:text-4xl">{t("browse_jobs")}</h1>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={() => { const n = new URLSearchParams(sp); n.delete("category"); setSp(n); }}
          data-testid="filter-all"
          className={`rounded-full border-2 px-4 py-2 text-sm font-bold ${!active ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white"}`}
        >
          {t("all")}
        </button>
        {cats.map((c) => (
          <button
            key={c.key}
            data-testid={`filter-${c.key}`}
            onClick={() => { const n = new URLSearchParams(sp); n.set("category", c.key); setSp(n); }}
            className={`rounded-full border-2 px-4 py-2 text-sm font-bold ${active === c.key ? "border-sarthi-primary bg-sarthi-accent text-sarthi-primary" : "border-sarthi-border bg-white"}`}
          >
            {nameFor(c)}
          </button>
        ))}
        <button
          data-testid="filter-verified-only"
          onClick={toggleVerifiedOnly}
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full border-2 px-4 py-2 text-sm font-bold transition ${verifiedOnly ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-sarthi-border bg-white text-sarthi-muted hover:text-emerald-700"}`}
          title="Show only jobs from verified recruiters"
        >
          <BadgeCheck className="h-4 w-4" strokeWidth={2.5} /> Verified only
        </button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <form onSubmit={applySearch} className="sarthi-card flex items-center gap-3 p-3">
          <Search className="h-5 w-5 flex-shrink-0 text-sarthi-muted" />
          <input
            data-testid="jobs-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "hi" ? "काम खोजें…" : lang === "mr" ? "काम शोधा…" : lang === "ta" ? "வேலை தேட…" : "Search jobs, skills…"}
            className="w-full bg-transparent px-1 py-2 text-base font-semibold focus:outline-none"
          />
          {queryParam && (
            <button
              type="button"
              data-testid="jobs-search-clear"
              onClick={() => { setQ(""); const n = new URLSearchParams(sp); n.delete("q"); setSp(n); }}
              className="text-xs font-bold uppercase text-sarthi-muted hover:text-sarthi-primary"
            >
              Clear
            </button>
          )}
        </form>

        <div className="sarthi-card flex items-center gap-3 p-3">
          <MapPin className="h-5 w-5 flex-shrink-0 text-sarthi-muted" />
          <input
            data-testid="location-filter"
            value={loc}
            onChange={(e) => setLoc(e.target.value)}
            placeholder={t("location")}
            className="w-full bg-transparent px-1 py-2 text-base font-semibold focus:outline-none"
          />
          {loc && <MapPin className="h-4 w-4 text-sarthi-primary" />}
        </div>
      </div>

      {visibleJobs.length === 0 ? (
        <div className="mt-10 text-center text-lg font-semibold text-sarthi-muted">{t("no_jobs")}</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleJobs.slice(0, 6).map((j) => <JobCard key={j.id} job={j} />)}
          {visibleJobs.length >= 6 && (
            <div className="sm:col-span-2 lg:col-span-3">
              <AdSlot slot="8396500342" layout="in-feed" layoutKey="-6t+ed+2i-1n-4w" />
            </div>
          )}
          {visibleJobs.slice(6).map((j) => <JobCard key={j.id} job={j} />)}
        </div>
      )}
    </div>
  );
}
